import {MdAnchorSection, MdFrameStick, joinedRootId, sticksById} from "../../model/frame/MdFrameBlock";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";
import {inches} from "../../const";
import {OutlineSide, outlineStickCoverage, outlineStickSides, voidMullionSides} from "./BlockOps";

// frame anchors on the outline sticks: each anchored stick splits into 1+ sections along
// its length, each with one anchor product and a qty. See anchor-setup-plan.md.

// the auto anchor spacing. anchorAutoQty is the one place that uses it, so the real
// per-length rule can replace it later.
const AUTO_SPACING = 36 * inches;

// the shortest a section may be dragged to, and the step a divider drag snaps to.
export const MIN_SECTION_LENGTH = 6 * inches;
const DIVIDER_SNAP = inches / 8;

// the qty of anchors a section of `length` (drawing units) gets when the user has not set one.
export function anchorAutoQty(length: number): number {
    if(length <= 0) return 0;
    return Math.ceil(length / AUTO_SPACING);
}

// one section laid out along a stick of a known length: its range and anchor positions
// (drawing units from the stick's A end).
export type AnchorSectionLayout = {
    index: number;
    start: number;
    end: number;
    product: number;
    qty: number;
    auto: boolean;
    positions: number[];
};

// lay a stick's sections out along `length`. `n` anchors in a section of length L are
// spaced s = L / n apart, with half a space at each end.
export function layoutAnchorSections(sections: MdAnchorSection[], length: number): AnchorSectionLayout[] {
    const out: AnchorSectionLayout[] = [];
    let start = 0;

    sections.forEach((section, index) => {
        const end = index === sections.length - 1 ? length : start + section.length * length;
        const span = end - start;
        const qty = section.qty ?? anchorAutoQty(span);
        const spacing = qty > 0 ? span / qty : 0;

        const positions: number[] = [];
        for(let i = 0; i < qty; i++) positions.push(start + spacing / 2 + i * spacing);

        out.push({index, start, end, product: section.product, qty, auto: section.qty === undefined, positions});
        start = end;
    });

    return out;
}

// the sticks that can take anchors, with the outline side each faces: the outline sticks
// and the mullions beside a void. A joined stick is left out: its anchors belong to its
// join root.
export function anchorStickSides(spec: MdFrameSpec): Map<string, OutlineSide> {
    const sticks = sticksById(spec.frameBlocks);
    const out = new Map<string, OutlineSide>();
    for(const [id, side] of outlineStickSides(spec)) {
        if(joinedRootId(sticks, id) === id) out.set(id, side);
    }
    for(const [id, side] of voidMullionSides(spec)) out.set(id, side);
    return out;
}

// true when a drawn piece of stick `root` carries its anchors. `pieceVoidSide` is the side
// of the piece a void lies on (see StickMaterial.voidSide). A mullion beside a void carries
// them only on the pieces beside the void; every other stick on all its pieces.
export function pieceTakesAnchors(spec: MdFrameSpec, root: string, pieceVoidSide: OutlineSide | null): boolean {
    const side = voidMullionSides(spec).get(root);
    return side === undefined || side === pieceVoidSide;
}

// the stick whose anchors a tap on a piece of stick `stickId` edits (its join root), or
// null when the piece can't take anchors. See pieceTakesAnchors for `pieceVoidSide`.
export function anchorTarget(spec: MdFrameSpec, stickId: string, pieceVoidSide: OutlineSide | null): string | null {
    const root = joinedRootId(sticksById(spec.frameBlocks), stickId);
    if(!anchorStickSides(spec).has(root)) return null;
    return pieceTakesAnchors(spec, root, pieceVoidSide) ? root : null;
}

function findStick(spec: MdFrameSpec, stickId: string): MdFrameStick | undefined {
    return sticksById(spec.frameBlocks).get(stickId);
}

// give a stick with no anchors one section over its full length.
export function addAnchors(spec: MdFrameSpec, stickId: string, product: number): boolean {
    const stick = findStick(spec, stickId);
    if(!stick || stick.anchors) return false;

    stick.anchors = {sections: [{length: 1, product}]};
    return true;
}

export function clearAnchors(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStick(spec, stickId);
    if(!stick?.anchors) return false;

    stick.anchors = undefined;
    return true;
}

// set the product of one section, or of every section of the stick when `index` is null.
export function setAnchorProduct(spec: MdFrameSpec, stickId: string, index: number | null, product: number): boolean {
    const sections = findStick(spec, stickId)?.anchors?.sections;
    if(!sections) return false;

    sections.forEach((s, i) => {
        if(index === null || index === i) s.product = product;
    });
    return true;
}

// set the qty of one section; undefined puts it back to auto.
export function setAnchorQty(spec: MdFrameSpec, stickId: string, index: number, qty: number | undefined): boolean {
    const section = findStick(spec, stickId)?.anchors?.sections[index];
    if(!section) return false;

    section.qty = qty === undefined ? undefined : Math.max(0, Math.round(qty));
    return true;
}

// set every section in the diagram to `product`.
export function applyAnchorProductToAll(spec: MdFrameSpec, product: number): boolean {
    let changed = false;
    for(const stick of sticksById(spec.frameBlocks).values()) {
        for(const s of stick.anchors?.sections ?? []) {
            s.product = product;
            changed = true;
        }
    }
    return changed;
}

// change a stick's section count. More sections: the stick splits into `count` equal
// sections, each with the product of the old section it starts in; a stick with a manual
// qty shares it out (ceil(total / count) each), else every section stays auto. Fewer
// sections: the removed sections merge into the last one kept. `length` is the stick's
// drawn length (drawing units), for the resolved qtys.
export function setAnchorSectionCount(spec: MdFrameSpec, stickId: string, count: number, length: number): boolean {
    const anchors = findStick(spec, stickId)?.anchors;
    if(!anchors || count < 1 || count === anchors.sections.length) return false;

    const old = anchors.sections;
    const laid = layoutAnchorSections(old, length);
    const manual = old.some(s => s.qty !== undefined);
    const total = laid.reduce((sum, s) => sum + s.qty, 0);

    if(count < old.length) {
        const kept = old.slice(0, count).map(s => ({...s}));
        const merged = old.slice(count - 1);
        const last = kept[count - 1];
        last.length = merged.reduce((sum, s) => sum + s.length, 0);
        last.qty = merged.some(s => s.qty !== undefined)
            ? laid.slice(count - 1).reduce((sum, s) => sum + s.qty, 0)
            : undefined;
        anchors.sections = kept;
        return true;
    }

    const next: MdAnchorSection[] = [];
    let start = 0;
    for(let i = 0; i < count; i++) {
        const from = old[sectionAt(old, start)];
        next.push({
            length: 1 / count,
            product: from.product,
            qty: manual ? Math.ceil(total / count) : undefined,
        });
        start += 1 / count;
    }
    anchors.sections = next;
    return true;
}

// the index of the section that holds fraction `at` of the stick.
function sectionAt(sections: MdAnchorSection[], at: number): number {
    let end = 0;
    for(let i = 0; i < sections.length; i++) {
        end += sections[i].length;
        if(at < end - 1e-9) return i;
    }
    return sections.length - 1;
}

// move the boundary after section `index` to `along` (drawing units from the stick's A
// end). Snaps to DIVIDER_SNAP and keeps both sections at least MIN_SECTION_LENGTH long.
// `length` is the stick's drawn length.
export function moveAnchorDivider(spec: MdFrameSpec, stickId: string, index: number, along: number, length: number): boolean {
    const sections = findStick(spec, stickId)?.anchors?.sections;
    if(!sections || index < 0 || index >= sections.length - 1 || length <= 0) return false;

    const before = sections.slice(0, index).reduce((sum, s) => sum + s.length, 0) * length;
    const after = before + (sections[index].length + sections[index + 1].length) * length;
    const min = before + MIN_SECTION_LENGTH;
    const max = after - MIN_SECTION_LENGTH;
    if(min > max) return false;

    const snapped = Math.round(along / DIVIDER_SNAP) * DIVIDER_SNAP;
    const at = Math.min(max, Math.max(min, snapped));
    const pair = sections[index].length + sections[index + 1].length;
    sections[index].length = (at - before) / length;
    sections[index + 1].length = pair - sections[index].length;
    return true;
}

// put a stick back to one auto section with the first product among `fromIds`' anchors.
// Used when a Split By or Join changes which part of the outline a stick covers.
export function resetAnchors(spec: MdFrameSpec, stickId: string, fromIds: string[]) {
    const sticks = sticksById(spec.frameBlocks);
    const stick = sticks.get(stickId);
    if(!stick) return;

    const product = fromIds.map(id => sticks.get(id)?.anchors?.sections[0]?.product).find(p => p !== undefined);
    stick.anchors = product === undefined ? undefined : {sections: [{length: 1, product}]};
}

// keep the anchors on sticks that can take them, after a tree change. A stick a merge
// dropped (`replaced`: old id -> the id that took its place) hands its anchors on; the
// stick that takes them gets one auto section, the same as a Join. `before` is every stick
// from before the render-time changes. A stick that now covers other cells than at the last
// call (a step, a stick moved off a void) starts over as one auto section. A stick that can
// no longer take anchors drops them.
export function normalizeAnchors(spec: MdFrameSpec, before: Map<string, MdFrameStick>, replaced: Map<string, string>) {
    const sticks = sticksById(spec.frameBlocks);

    for(const [from, to] of replaced) {
        const product = before.get(from)?.anchors?.sections[0]?.product;
        const target = sticks.get(to);
        if(product === undefined || !target || target.anchors) continue;
        target.anchors = {sections: [{length: 1, product}]};
    }

    const coverage = outlineStickCoverage(spec);
    for(const stick of sticks.values()) {
        const was = spec.anchorCoverage.get(stick.id);
        if(!stick.anchors || was === undefined || was === coverage.get(stick.id)) continue;
        resetAnchors(spec, stick.id, [stick.id]);
    }
    spec.anchorCoverage = coverage;

    const eligible = anchorStickSides(spec);
    for(const stick of sticks.values()) {
        if(stick.anchors && (!eligible.has(stick.id) || stick.anchors.sections.length === 0)) stick.anchors = undefined;
    }
}
